import React from "react";
import { useAuth } from "./AuthContext";
import { Building2, Loader2 } from "lucide-react";

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
    <div>
      <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

export const Btn = ({ variant = "primary", className = "", children, ...props }) => {
  const variants = {
    primary: "bg-emerald-600 hover:bg-emerald-500 text-white",
    outline: "bg-white border border-slate-200 hover:bg-slate-50 text-slate-700",
    danger: "bg-red-600 hover:bg-red-500 text-white",
    ghost: "hover:bg-slate-100 text-slate-600",
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const Card = ({ className = "", children, ...props }) => (
  <div className={`bg-white border border-slate-200 rounded-xl shadow-sm ${className}`} {...props}>{children}</div>
);

export const Input = React.forwardRef(({ className = "", ...props }, ref) => (
  <input
    ref={ref}
    className={`w-full px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-colors text-sm ${className}`}
    {...props}
  />
));

export const Textarea = React.forwardRef(({ className = "", rows = 10, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={`w-full px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-colors text-sm resize-y max-h-[60vh] overflow-auto ${className}`}
    {...props}
  />
));

export const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    {children}
  </div>
);

export const Badge = ({ color = "slate", children }) => {
  const map = {
    slate: "bg-slate-100 text-slate-600",
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${map[color] || map.slate}`}>{children}</span>;
};

export const Spinner = () => (
  <div className="flex items-center justify-center py-16 text-slate-400">
    <Loader2 className="w-6 h-6 animate-spin" />
  </div>
);

export const Empty = ({ icon: Icon, title, hint }) => (
  <div className="text-center py-16">
    {Icon && <Icon className="w-10 h-10 mx-auto text-slate-300 mb-3" />}
    <p className="text-slate-600 font-medium">{title}</p>
    {hint && <p className="text-sm text-slate-400 mt-1">{hint}</p>}
  </div>
);

export const RequireTenant = ({ children }) => {
  const { isSuperAdmin, activeTenant } = useAuth();
  if (isSuperAdmin && !activeTenant) {
    return (
      <Empty
        icon={Building2}
        title="Selecteaza un tenant"
        hint="Foloseste selectorul din dreapta sus pentru a alege compania cu care lucrezi."
      />
    );
  }
  return children;
};
